import PropTypes from 'prop-types';
import {
  trans,
  useSoyuzClientConfig,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';
import { PrivateLandlord, useCityName } from '@spotahome/ui-library';
import { Mediator } from '@spotahome/soyuz-mediator';
import { useInView } from 'react-intersection-observer';

import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import { OPEN_PHOTO_SLIDER_SECTION } from '@spotahome/marketplace-common/src/channels';
import {
  getStaticMapUrl,
  getStaticMapUrlMapbox,
  getStaticMapUrlWoosmap
} from '@spotahome/marketplace-common/src/utils/static-map';

import { ListingSectionObservable } from '../ListingSection';

import './ListingLocation.scss';

const unique = items => {
  const seen = [];
  return items
    .map(item => item.trim())
    .filter(item => {
      if (seen.includes(item.toLowerCase())) return false;
      seen.push(item.toLowerCase());
      return true;
    });
};

const ListingLocation = ({ listing, bordered = true }) => {
  const { googleMapsApiKey, mapboxAccessToken, woosmapApiKey } =
    useSoyuzClientConfig();
  const mapProviderVariant = useSoyuzExperiment('MapProviderOnlyWoosmap').get(
    'behaviour'
  );
  const cityName = useCityName(listing.cityId);
  const [lng, lat] = listing.coords;
  const zoom = 14;

  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '300px' });

  const handleOpenMap = () => {
    Mediator.publish(OPEN_PHOTO_SLIDER_SECTION, 'map');
  };

  const buildMapUrl = () => {
    const base = { lat, lng, zoom, size: '640x360' };

    if (mapProviderVariant === 'mapbox') {
      return getStaticMapUrlMapbox({ ...base, accessToken: mapboxAccessToken });
    }

    if (mapProviderVariant === 'woosmap') {
      return getStaticMapUrlWoosmap({ ...base, apiKey: woosmapApiKey });
    }

    return getStaticMapUrl({ ...base, apiKey: googleMapsApiKey });
  };

  const mapUrl = inView ? buildMapUrl() : null;

  const cityAndArea = unique([cityName, listing.area, listing.address])
    .filter(Boolean)
    .join(', ');

  return (
    <ListingSectionObservable
      section="location"
      className="listing-location"
      bordered={bordered}
    >
      <ListingTitle role="secondary">
        {trans('listing.location.title')}
      </ListingTitle>
      <p className="listing-location__city">{cityAndArea}</p>
      <p className="listing-location__address">
        {trans('listing.location.exact_address')}
      </p>
      <button
        ref={ref}
        className="listing-location__map-button"
        onClick={handleOpenMap}
        type="button"
        aria-label={cityName}
      >
        {mapUrl && (
          <>
            <img
              className="listing-location__map-image"
              src={mapUrl}
              alt={cityName}
            />
            <div className="listing-location__map-overlay" />
            <div className="listing-location__map-icon">
              <PrivateLandlord />
            </div>
          </>
        )}
      </button>
    </ListingSectionObservable>
  );
};

ListingLocation.propTypes = {
  listing: PropTypes.object.isRequired,
  bordered: PropTypes.bool
};

export default ListingLocation;
